import React, { useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { Button, MenuItem } from '@blueprintjs/core';
import { Select } from '@blueprintjs/select';
import { NumericFormat } from 'react-number-format';
import { DateInput3 as DateInput } from '@blueprintjs/datetime2';
import { enUS } from 'date-fns/locale';

import { toast as message } from 'sonner';

import { getNestedValue } from '../../../utils/common';
import { validateAndNormalizePhone, trimEmail, isValidEmail } from '../../../utils/search';
import { useAuth } from '../../../utils/useAuth';
import { addPicklistValue, updateOrCreatePicklist } from '../../../utils/metadata/picklists';
import { useMyCapabilities } from '../../../utils/metadata/capabilities';
import { invalidateAllMetadataCache } from '../../../utils/metadata';
import { useGlobalMetadata } from '../../../utils/providers';
import { toFormFieldValue } from '../utils/fieldValueUtils';
import RecordLookupSelect from '../../RecordLookupSelect';
import MultiSelect from '../../MultiSelect';
import RecordLink from '../../links/RecordLink';
import FileFieldInput from './FileFieldInput';

import type {
  EnhancedFormFieldValues,
  LegacyFormFieldObject,
  RecordFormMetadata,
  DatabaseDataType
} from '../../../types/record-form';
import type { FilePointer, FormFieldValue } from '../../../types/forms';
import type { DynamicRecord } from '../../../types/api';

interface RecordFieldInputProps {
  fieldObj: LegacyFormFieldObject;
  fieldKey: string;
  fieldValues: EnhancedFormFieldValues;
  fieldErrors: Record<string, string | null>;
  setFieldValues: Dispatch<SetStateAction<EnhancedFormFieldValues>>;
  setFieldErrors: Dispatch<SetStateAction<Record<string, string | null>>>;
  processedMetadata: RecordFormMetadata;
  readOnly: boolean;
  /** Render a selectable, copyable value instead of a disabled control. */
  displayOnly?: boolean;
  isDeleted: boolean;
  schemaName: string;
  tableName?: string;
  recordId?: string | number | null;
  /** Autosave handlers resolve once the write settles; staged handlers return void. */
  onFieldUpdate: (fieldName: string, value: FormFieldValue, previousValue?: FormFieldValue) => void | Promise<void>;
  onDebouncedFieldUpdate: (fieldName: string, value: FormFieldValue) => void;
  onDebouncedEmailUpdate: (fieldName: string, value: string) => void;
  onCancelDebouncedField: (fieldName: string) => void;
}

const RecordFieldInput: React.FC<RecordFieldInputProps> = ({
  fieldObj,
  fieldKey,
  fieldValues,
  fieldErrors,
  setFieldValues,
  setFieldErrors,
  processedMetadata,
  readOnly,
  displayOnly = false,
  isDeleted,
  schemaName,
  tableName,
  recordId,
  onFieldUpdate,
  onDebouncedFieldUpdate,
  onDebouncedEmailUpdate,
  onCancelDebouncedField
}) => {
  const { field, dbCol } = fieldObj;
  const fieldName = field;
  // Only super admins may maintain picklist option lists — the same gate the
  // server enforces in core_crm.update_or_create_picklist_and_values.
  const { isAdmin } = useAuth();
  const { hasCapability } = useMyCapabilities();
  // Adding a multi-select option needs add_any_picklist_values, or the
  // capability the table's metadata names (data-entry tables name
  // add_data_entry_picklist_values). core_crm.add_picklist_value enforces both.
  const { data: globalMetadata } = useGlobalMetadata();
  const tableAddCapability = tableName ? globalMetadata?.tableData?.byName?.[tableName]?.ui_data?.picklist_add_capability : undefined;
  const canAddOptions = hasCapability('add_any_picklist_values')
    || (typeof tableAddCapability === 'string' && hasCapability(tableAddCapability));
  const dataType = dbCol.data_type as DatabaseDataType;
  const uiLabel = fieldObj.label || dbCol.ui_label || fieldName;
  const required = dbCol.is_required;
  const isReadOnly = readOnly || isDeleted || dbCol.is_calculated || dbCol.is_system || fieldObj.editable === false;

  let disabledTitle = '';
  if (readOnly) {
    disabledTitle = 'Form is in read-only mode.';
  } else if (isDeleted) {
    disabledTitle = 'This record has been deleted.';
  } else if (fieldObj.editable === false) {
    disabledTitle = 'This field is not editable.';
  } else if (dbCol.is_calculated) {
    disabledTitle = 'This field is calculated and cannot be edited.';
  } else if (dbCol.is_system) {
    disabledTitle = 'This field is system-generated and cannot be edited.';
  }

  const value = getNestedValue(fieldValues, field);
  const relationship = processedMetadata.relationships?.[fieldName];

  const renderDisplayInput = (displayValue: unknown): React.ReactElement => (
    <input
      key={fieldKey}
      className="sky-field-input"
      id={fieldName}
      name={fieldName}
      value={Array.isArray(displayValue) ? displayValue.join(', ') : String(displayValue ?? '')}
      readOnly
      aria-readonly="true"
      style={{ width: '100%', cursor: 'text' }}
    />
  );

  const renderInput = useCallback((): React.ReactElement => {
    if (displayOnly) {
      if (relationship) {
        const relationshipRecord = fieldValues[`${fieldName}_rel`] as DynamicRecord | null;
        return renderDisplayInput(relationshipRecord?.[relationship.target_label_field] ?? value);
      }

      const picklist = processedMetadata.picklists?.[fieldName];
      if (picklist) {
        const selectedValues = Array.isArray(value) ? value : [value];
        const displayValue = selectedValues
          .map(selectedValue => picklist.values?.find((option: any) => option.value === selectedValue)?.label || selectedValue)
          .filter(Boolean);
        return renderDisplayInput(displayValue);
      }

      return renderDisplayInput(dataType === 'boolean' ? (value ? 'Yes' : 'No') : value);
    }

    // Relationship field rendering
    if (relationship) {
      // Assertive null check: relationships MUST have valid target_table_name
      if (!relationship.target_table_name) {
        throw new Error(`Invalid relationship for field ${fieldName}: missing target_table_name`);
      }

      const isClientSide = processedMetadata.clientSideTables.includes(relationship.target_table_name);

      if (isClientSide) {
        const options = processedMetadata.clientSideSelectTables[relationship.target_table_name] || [];
        const selectedItem = options.find((option: any) => option.id === value);
        const targetTableUiLabel = processedMetadata.tableData.byName[relationship.target_table_name]?.ui_label;

        return (
          <div
            key={fieldKey}
            className="record-link-container"
            style={{
              position: 'relative',
              width: '100%'
            }}
          >
            {selectedItem && (
              <RecordLink
                tableName={relationship.target_table_name}
                recordId={selectedItem.id}
                className="record-link-overlay"
              />
            )}
            <Select
              items={options}
              disabled={isReadOnly}
              itemRenderer={(item: any, { handleClick, modifiers }) => {
                if (!modifiers.matchesPredicate) {
                  return null;
                }
                return (
                  <MenuItem
                    key={item.id}
                    text={item[relationship.target_label_field]}
                    onClick={handleClick}
                    active={modifiers.active}
                    disabled={modifiers.disabled}
                  />
                );
              }}
              onItemSelect={(item: any) => {
                const newValue = item.id;
                const previousValue = toFormFieldValue(fieldValues[fieldName]);
                onFieldUpdate(fieldName, newValue, previousValue);
              }}
              itemPredicate={(query: string, item: any) => {
                const label = String(item[relationship.target_label_field])?.toLowerCase() || '';
                return label.includes(String(query).toLowerCase());
              }}
              popoverProps={{ portalClassName: 'sky-elevated-portal' }}
              filterable
              fill
            >
              <Button
                className={selectedItem ? undefined : 'sky-select-placeholder'}
                text={
                  selectedItem
                    ? selectedItem[relationship.target_label_field]
                    : `Select a ${targetTableUiLabel || relationship.target_table_name}`
                }
                rightIcon="double-caret-vertical"
                fill
                disabled={isReadOnly}
              />
            </Select>
          </div>
        );
      }

      // Server-side relationship lookup
      const relationshipRecord = fieldValues[`${fieldName}_rel`] as DynamicRecord | null;
      const routeName = processedMetadata.routeData.tableToRouteName[relationship.target_table_name];

      return (
        <RecordLookupSelect
          key={fieldKey}
          routeName={routeName}
          disabled={isReadOnly}
          title={disabledTitle}
          name={fieldName}
          record={relationshipRecord}
          relationship={relationship}
          schema={schemaName}
          onChange={(record: DynamicRecord | null) => {
            if (record) {
              const previousValue = toFormFieldValue(fieldValues[fieldName]);
              setFieldValues((prev) => ({
                ...prev,
                [`${fieldName}_rel`]: record
              } as EnhancedFormFieldValues));
              onFieldUpdate(fieldName, record.id, previousValue);
            }
          }}
        />
      );
    }

    // Multi-select picklist rendering
    const picklistConfig = processedMetadata.picklists?.[fieldName];
    if (picklistConfig && picklistConfig.type === 'Multi Select') {
      const options = picklistConfig.values || [];

      // Staff allowed to add options (canAddOptions) type a new one to add it (REG-792,
      // e.g. a missing document). Super admins also rename or delete options
      // through the admin-gated RPC the /admin picklist editor uses.
      //
      // Write ordering is dictated by the metadata-driven picklist CHECK
      // constraint: a record write may only carry registered values, and
      // unregistering a value re-validates every existing row. So a value is
      // registered BEFORE any selection write uses it, and removed from this
      // record BEFORE the option itself is removed. Values other records hold
      // still block removal server-side — that failure is surfaced as such.
      const saveOptionValues = async (nextValues: string[], successMessage: string | null, inUseLabel?: string): Promise<boolean> => {
        try {
          await updateOrCreatePicklist({
            schema: schemaName,
            tableName: tableName || '',
            columnName: fieldName,
            values: nextValues,
            picklistType: 'Multi Select',
          });
          invalidateAllMetadataCache();
          if (successMessage) message.success(successMessage);
          return true;
        } catch (error) {
          console.error('Failed to update picklist options:', error);
          const raw = String((error as Error)?.message || '');
          message.error(inUseLabel && (raw.includes('must be present in') || raw.includes('still in use'))
            ? `Cannot remove "${inUseLabel}". It is still selected on other records.`
            : 'Failed to update the option list.');
          return false;
        }
      };
      const optionLabels = () => options.map((option: any) => String(option.value));
      const selectedValues = () => (Array.isArray(value) ? (value as (string | number)[]) : []);
      // With a live-save or autosave handler this resolves once the record
      // write settles, which the option-removal steps below depend on.
      const updateSelection = (nextSelected: (string | number)[]) => {
        const previousValue = toFormFieldValue(fieldValues[fieldName]);
        return onFieldUpdate(fieldName, nextSelected, previousValue);
      };
      const canEditOptions = !picklistConfig.is_system && tableName && !isReadOnly;
      const addOption = async (label: string) => {
        // Typing a name that is already an option selects that option, under
        // its stored spelling, instead of registering a duplicate.
        const existing = options.find((option: any) => String(option.value).toLowerCase() === label.toLowerCase());
        if (existing) {
          if (!selectedValues().includes(existing.value)) {
            await updateSelection([...selectedValues(), existing.value]);
          }
          return;
        }
        let stored: string;
        try {
          stored = await addPicklistValue({ schema: schemaName, tableName: tableName || '', columnName: fieldName, value: label });
        } catch (error) {
          console.error('Failed to add picklist option:', error);
          message.error(`Could not add "${label}" to the option list.`);
          return;
        }
        invalidateAllMetadataCache();
        message.success(`Added "${stored}"`);
        // A freshly added document is what the deal is waiting on, so select it.
        if (!selectedValues().includes(stored)) {
          await updateSelection([...selectedValues(), stored]);
        }
      };
      const optionTools = !canEditOptions ? undefined : isAdmin ? {
        onAdd: addOption,
        onRename: async (optionValue: string | number, nextLabel: string) => {
          const others = optionLabels().filter((existing) => existing !== String(optionValue));
          if (others.some((existing) => existing.toLowerCase() === nextLabel.toLowerCase())) {
            message.error(`"${nextLabel}" is already an option.`);
            return;
          }
          // Register the new name alongside the old one, repoint this record,
          // then retire the old name once nothing here references it.
          const renamed = optionLabels().map((existing) => (existing === String(optionValue) ? nextLabel : existing));
          if (!await saveOptionValues([...optionLabels(), nextLabel], null)) return;
          const selected = selectedValues();
          if (selected.includes(optionValue)) {
            await updateSelection(selected.map((v) => (v === optionValue ? nextLabel : v)));
          }
          await saveOptionValues(renamed, `Renamed to "${nextLabel}"`, String(optionValue));
        },
        onDelete: async (optionValue: string | number) => {
          // Deselect here first — unregistering a value this record still
          // holds would fail the constraint re-validation.
          const selected = selectedValues();
          if (selected.includes(optionValue)) {
            await updateSelection(selected.filter((v) => v !== optionValue));
          }
          await saveOptionValues(
            optionLabels().filter((existing) => existing !== String(optionValue)),
            `Deleted "${optionValue}"`,
            String(optionValue),
          );
        },
      } : canAddOptions ? { onAdd: addOption } : undefined;

      return (
        <MultiSelect
          key={fieldKey}
          disabled={isReadOnly}
          id={fieldName}
          options={options}
          value={value}
          onUpdate={(updatedValues: (string | number)[]) => {
            const previousValue = toFormFieldValue(fieldValues[fieldName]);
            onFieldUpdate(fieldName, updatedValues, previousValue);
          }}
          uiLabel={uiLabel}
          required={required || undefined}
          optionTools={optionTools}
        />
      );
    }

    // Single-select picklist rendering
    if (picklistConfig) {
      const options = picklistConfig.values || [];
      const optionsWithClear = !required
        ? [{ value: null, label: '🚫 Clear' }, ...options]
        : options;

      return (
        <Select
          key={fieldKey}
          disabled={isReadOnly}
          items={optionsWithClear}
          itemRenderer={(item: any, { handleClick, modifiers }) => {
            if (!modifiers.matchesPredicate) {
              return null;
            }
            return (
              <MenuItem
                disabled={isReadOnly}
                key={item.value ?? 'none'}
                text={item.label || item.value || 'None'}
                onClick={handleClick}
                active={modifiers.active}
              />
            );
          }}
          onItemSelect={(item: any) => {
            const previousValue = toFormFieldValue(fieldValues[fieldName]);
            onFieldUpdate(fieldName, item.value, previousValue);
          }}
          itemPredicate={(query: string, item: any) => {
            const label = String(item.label || item.value || 'None').toLowerCase();
            return label.includes(query.toLowerCase());
          }}
          popoverProps={{ portalClassName: 'sky-elevated-portal' }}
          filterable
          fill
        >
          <Button
            className={value ? undefined : 'sky-select-placeholder'}
            text={
              value
                ? optionsWithClear.find((option: any) => option.value === value)?.label || value
                : `Select ${uiLabel}`
            }
            disabled={isReadOnly}
            rightIcon="double-caret-vertical"
            fill
          />
        </Select>
      );
    }

    // File field rendering
    if ((dbCol as any).is_file) {
      const files: FilePointer[] = Array.isArray(value) ? (value as FilePointer[]) : [];
      const allowMultiple = (dbCol.ui_data as any)?.multiple ?? false;
      const allowedMimeTypes: string[] = (dbCol.ui_data as any)?.allowed_mime_types ?? [];
      return (
        <FileFieldInput
          key={fieldKey}
          fieldName={fieldName}
          schemaName={schemaName}
          tableName={tableName || ''}
          recordId={recordId ?? null}
          value={files}
          allowMultiple={allowMultiple}
          allowedMimeTypes={allowedMimeTypes}
          isReadOnly={isReadOnly}
          onUpdate={(newValue) => {
            const previousValue = toFormFieldValue(fieldValues[fieldName]);
            onFieldUpdate(fieldName, newValue, previousValue);
          }}
        />
      );
    }

    // Handle email fields with validation
    if (dbCol.is_email) {
      return (
        <input
          key={fieldKey}
          className={`sky-field-input${fieldErrors[fieldName] ? ' sky-field-error' : ''}`}
          title={disabledTitle}
          disabled={isReadOnly}
          id={fieldName}
          name={fieldName}
          value={(value as string) || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const inputValue = e.target.value;
            setFieldValues((prev) => ({ ...prev, [fieldName]: inputValue } as EnhancedFormFieldValues));
            setFieldErrors((prev) => ({ ...prev, [fieldName]: null }));
            onDebouncedEmailUpdate(fieldName, inputValue);
          }}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            const inputValue = e.target.value;
            const trimmed = trimEmail(inputValue);

            onCancelDebouncedField(fieldName);

            if (trimmed && !isValidEmail(trimmed)) {
              setFieldErrors((prev) => ({ ...prev, [fieldName]: 'Invalid email format' }));
              return;
            }

            setFieldErrors((prev) => ({ ...prev, [fieldName]: null }));
            const previousValue = toFormFieldValue(fieldValues[fieldName]);
            onFieldUpdate(fieldName, trimmed, previousValue);
          }}
          style={{ width: '100%' }}
          required={required || undefined}
        />
      );
    }

    // Handle phone fields with validation
    if (dbCol.is_phone) {
      return (
        <input
          key={fieldKey}
          className={`sky-field-input${fieldErrors[fieldName] ? ' sky-field-error' : ''}`}
          title={disabledTitle}
          disabled={isReadOnly}
          id={fieldName}
          name={fieldName}
          value={(value as string) || ''}
          placeholder="e.g., (555) 123-4567 or 5551234567"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const inputValue = e.target.value;
            setFieldValues((prev) => ({ ...prev, [fieldName]: inputValue } as EnhancedFormFieldValues));

            if (!inputValue || inputValue.trim() === '') {
              setFieldErrors((prev) => ({ ...prev, [fieldName]: null }));
            } else {
              const result = validateAndNormalizePhone(inputValue);
              if (!result.valid) {
                setFieldErrors((prev) => ({ ...prev, [fieldName]: result.error }));
              } else {
                setFieldErrors((prev) => ({ ...prev, [fieldName]: null }));
              }
            }
          }}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            const inputValue = e.target.value;

            if (!inputValue || inputValue.trim() === '') {
              setFieldErrors((prev) => ({ ...prev, [fieldName]: null }));
              const previousValue = toFormFieldValue(fieldValues[fieldName]);
              onFieldUpdate(fieldName, null, previousValue);
              return;
            }

            const result = validateAndNormalizePhone(inputValue);

            if (!result.valid) {
              setFieldErrors((prev) => ({ ...prev, [fieldName]: result.error }));
              return;
            }

            setFieldErrors((prev) => ({ ...prev, [fieldName]: null }));
            const previousValue = toFormFieldValue(fieldValues[fieldName]);
            onFieldUpdate(fieldName, inputValue, previousValue);
          }}
          style={{ width: '100%' }}
          required={required || undefined}
        />
      );
    }

    // Standard field type rendering based on database data type
    switch (dataType) {
      case 'date':
      case 'timestamp without time zone':
      case 'timestamp with time zone':
        return (
          <DateInput
            key={fieldKey}
            locale={enUS}
            disabled={isReadOnly}
            popoverProps={{ portalClassName: 'sky-elevated-portal' }}
            value={value ? (value as string) : null}
            formatDate={(date: Date) => date.toLocaleDateString()}
            parseDate={(str: string) => new Date(str)}
            onChange={(newDate: string | null, isUserChange: boolean) => {
              if (isUserChange) {
                const previousValue = toFormFieldValue(fieldValues[fieldName]);
                onFieldUpdate(fieldName, newDate, previousValue);
              }
            }}
          />
        );

      case 'numeric':
      case 'integer':
      case 'bigint':
      case 'decimal': {
        const uiIsCurrency = dbCol.ui_is_currency;
        const uiIsPercentage = dbCol.ui_is_percentage;
        const uiScale = dbCol.ui_scale;
        const suffixOverride = typeof fieldObj.suffix === 'function'
          ? fieldObj.suffix(fieldValues)
          : fieldObj.suffix;

        return (
          <NumericFormat
            key={fieldKey}
            className="sky-field-input"
            title={disabledTitle}
            disabled={isReadOnly}
            id={fieldName}
            name={fieldName}
            value={value || ''}
            thousandSeparator
            prefix={uiIsCurrency ? '$' : ''}
            suffix={suffixOverride ?? (uiIsPercentage ? '%' : '')}
            decimalScale={uiScale || 0}
            fixedDecimalScale={false}
            allowNegative
            allowLeadingZeros={false}
            onValueChange={(values) => {
              const { floatValue } = values;
              if (floatValue !== undefined) {
                onDebouncedFieldUpdate(fieldName, floatValue);
              }
            }}
            inputMode="decimal"
            style={{ width: '100%' }}
            required={required || undefined}
          />
        );
      }

      case 'boolean':
        return (
          <input
            key={fieldKey}
            type="checkbox"
            className="sky-field-checkbox"
            title={disabledTitle}
            disabled={isReadOnly}
            id={fieldName}
            name={fieldName}
            checked={!!value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const checked = e.target.checked;
              const previousValue = toFormFieldValue(fieldValues[fieldName]);
              onFieldUpdate(fieldName, checked, previousValue);
            }}
          />
        );

      case 'text':
      case 'character varying':
      case 'varchar':
        return (
          <input
            key={fieldKey}
            className="sky-field-input"
            title={disabledTitle}
            disabled={isReadOnly}
            id={fieldName}
            name={fieldName}
            placeholder={fieldObj.placeholder}
            value={(value as string) || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const inputValue = e.target.value;
              onDebouncedFieldUpdate(fieldName, inputValue);
            }}
            style={{ width: '100%' }}
            required={required || undefined}
          />
        );

      default:
        return (
          <input
            key={fieldKey}
            className="sky-field-input"
            title={disabledTitle}
            disabled={isReadOnly}
            id={fieldName}
            name={fieldName}
            value={(value as string) || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const inputValue = e.target.value;
              onDebouncedFieldUpdate(fieldName, inputValue);
            }}
            style={{ width: '100%' }}
            required={required || undefined}
          />
        );
    }
  }, [
    relationship,
    fieldName,
    fieldKey,
    fieldValues,
    processedMetadata,
    value,
    isReadOnly,
    disabledTitle,
    schemaName,
    tableName,
    recordId,
    onFieldUpdate,
    onDebouncedFieldUpdate,
    onDebouncedEmailUpdate,
    onCancelDebouncedField,
    setFieldValues,
    setFieldErrors,
    dataType,
    dbCol,
    required,
    uiLabel,
    displayOnly,
    isAdmin
  ]);

  return renderInput();
};

export default RecordFieldInput;
